import { Grid } from "@material-ui/core";
import { observer } from "mobx-react";
import * as React from "react";
import { t } from "../../../i18n/util";
import { API } from "../../../network/API";
import { Note, PutApplicantNotesPayload } from "../../../network/APITypes";
import { generalStore } from "../../../stores/GeneralStore";
import { EMPTY_NOTE, hrStore } from "../../../stores/HrStore";
import { THEME } from "../../../theme/ThemeStore";
import { useUserInfo } from "../../hooks/useUserInfo";
import { ApplicantNote } from "../../ui/ApplicantNote";
import { ConfirmationDialog } from "../../ui/ConfirmationDialog";
import { NoNotes } from "../../ui/EmptyStates";
import { NoteDialog } from "../dialogs/ApplicantNotesDialogs";

export const ApplicantNotesSite = observer(() => {
    const [openNoteDialog, setOpenNoteDialog] = React.useState(false);
    const [noteToDelete, setNoteToDelete] = React.useState<Note>();
    const [initialValues, setInitialValues] = React.useState(EMPTY_NOTE);
    // toggle whether a note or comment is edited or created
    const [editId, setEditId] = React.useState<boolean>(false);
    // toggle whether a note or a comment is worked on
    const [commentNoteId, setCommentNoteId] = React.useState<string | undefined>(undefined);

    const { userInfo, loadFailed } = useUserInfo();

    //#region handle note dialog
    const handleOpenNoteDialog = (note?: Note) => {
        setInitialValues(note ?? EMPTY_NOTE);
        setEditId(!!note);
        setOpenNoteDialog(true);
    };

    const handleSubmitNoteDialog = async (model: PutApplicantNotesPayload) => {
        try {
            generalStore.isLoading = true;
            if (commentNoteId) {
                if (editId) {
                    await API.putNoteComment(hrStore.currentApplicantID, commentNoteId, initialValues.id, model);
                } else {
                    await API.putNoteComments(hrStore.currentApplicantID, commentNoteId, {
                        content: model.content,
                        mentions: model.mentions
                    });
                }
            } else {
                if (editId) {
                    await API.updateNote(hrStore.currentApplicantID, initialValues.id, model.content, model.title);
                } else {
                    await API.putNote(hrStore.currentApplicantID, model);
                }
            }

            await hrStore.loadApplicant();
        } catch (error) {
            if (editId) {
                generalStore.setError(t("error.updateNote"), error);
            } else {
                generalStore.setError(t("error.putNote"), error);
            }
        } finally {
            generalStore.isLoading = false;
            setOpenNoteDialog(false);
        }
    };

    const handleCloseNoteDialog = () => {
        setOpenNoteDialog(false);
    };
    //#endregion

    //#region handle delete note
    const handleOpenDeleteDialog = (note: Note) => {
        setNoteToDelete(note);
    };

    const handleDeleteNote = async () => {
        try {
            generalStore.isLoading = true;

            if (noteToDelete) {
                if (commentNoteId) {
                    await API.deleteComment(hrStore.currentApplicantID, commentNoteId, noteToDelete.id);
                } else {
                    await API.deleteNote(hrStore.currentApplicantID, noteToDelete.id);
                }
            }
            await hrStore.loadApplicant();
        } catch (err) {
            generalStore.setError(t("error.deleteNote"), err);
        } finally {
            generalStore.isLoading = false;
            setNoteToDelete(undefined);
        }
    };

    const handleCloseDeleteDialog = () => {
        setNoteToDelete(undefined);
    };
    //#endregion

    return (
        <>
            <div
                style={{
                    marginBottom: 104,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end"
                }}
            >
                {hrStore.currentApplicant.notes?.length === 0 && !generalStore.isLoading ? (
                    <NoNotes />
                ) : (
                    <Grid container spacing={4}>
                        {hrStore.currentApplicant.notes?.map((note) => (
                            <ApplicantNote
                                key={note.id}
                                note={note}
                                handleOpenNoteDialog={handleOpenNoteDialog}
                                handleOpenDeleteDialog={handleOpenDeleteDialog}
                                setComment={setCommentNoteId}
                                userInfo={userInfo}
                            />
                        ))}
                    </Grid>
                )}
                <img
                    src={THEME.Images.icAdd}
                    alt=""
                    style={{
                        cursor: "pointer",
                        height: 40,
                        width: 40,
                        position: "fixed",
                        bottom: 48
                    }}
                    onClick={() => {
                        handleOpenNoteDialog();
                        setCommentNoteId(undefined);
                    }}
                />
            </div>
            <NoteDialog
                isOpen={openNoteDialog}
                initialValues={initialValues}
                edit={editId}
                comment={commentNoteId}
                onSubmit={handleSubmitNoteDialog}
                onBack={handleCloseNoteDialog}
            />
            <ConfirmationDialog
                label={
                    commentNoteId
                        ? t("screen.applicantNotes.commentDeleteComfirmation")
                        : t("screen.applicantNotes.deleteConfirmation")
                }
                isOpen={!!noteToDelete}
                onBack={handleCloseDeleteDialog}
                onSubmit={handleDeleteNote}
            />
        </>
    );
});
